import { useEffect, useRef, useCallback } from 'react';

/**
 * useVisibilityAwareInterval
 *
 * Like setInterval, but pauses when the browser tab is hidden
 * and fires immediately when the tab becomes visible again.
 * Prevents wasted API calls / Firestore reads from idle tabs.
 */
export function useVisibilityAwareInterval(
  callback: () => void,
  intervalMs: number,
  enabled: boolean = true
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => callbackRef.current(), intervalMs);
  }, [intervalMs]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopInterval();
      return;
    }

    // Start the interval
    startInterval();

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        // Tab became visible — fire immediately and restart interval
        callbackRef.current();
        startInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, startInterval, stopInterval]);
}
